'use client';

import { memo, useEffect, useRef, useState, type CSSProperties } from 'react';
import clsx from 'clsx';

/**
 * BlurRevealImage, a high-res image that stays BLURRED until the browser has the
 * WHOLE image, then snaps into focus.
 *
 * The problem: a plain <img> of a 2K/4K output paints progressively (top-to-bottom)
 * as bytes arrive, which looks broken. The fix here is deliberately simple and
 * flicker-proof:
 *   - There is exactly ONE <img>. It is never removed, hidden, or swapped between
 *     layers, so it can never "disappear and come back". It only changes its CSS
 *     `filter` from a heavy blur to none.
 *   - While loading, a heavy blur (+ slight zoom) covers it. Whatever is behind the
 *     blur, the previous frame, or the new image painting in top-to-bottom, is
 *     hidden by the blur ("cheat behind the blur"), so the messy load is invisible.
 *   - The single source of truth for "sharp" is the element's own `load` event
 *     (optionally confirmed with decode()): the WHOLE image is ready → unblur.
 *
 * Because the reveal is driven by a real DOM event (not rAF or an animation clock),
 * it works even in background/inactive tabs; the blur→sharp is a plain CSS
 * transition, and if transitions are disabled the image simply snaps sharp on load.
 * Honors prefers-reduced-motion by skipping the blur transition.
 */
export type BlurRevealImageProps = {
  src: string;
  alt?: string;
  /**
   * Class for the wrapper. It is `overflow-hidden`; the CALLER must make it a
   * positioning context (add `relative`, or `absolute inset-0` to fill a parent)
   * so the image can fill it.
   */
  className?: string;
  /** Class applied to the <img> (e.g. `object-cover` / `object-contain`). */
  imgClassName?: string;
  /** Fires once the image has loaded, with its intrinsic size. */
  onReady?: (dims: { naturalWidth: number; naturalHeight: number }) => void;
  /** Fires if the image fails to load. */
  onError?: () => void;
  onClick?: () => void;
  draggable?: boolean;
  /** Native browser loading policy for repeated image grids. */
  loading?: 'eager' | 'lazy';
  /** Request priority for the current image candidate. */
  fetchPriority?: 'high' | 'low' | 'auto';
  /** Blur radius (px) shown while loading. "A lot of blur", default 32. */
  blurPx?: number;
  /** Blur→sharp transition duration (ms). */
  durationMs?: number;
  /**
   * Hold the blur for at least this long after the src starts loading, even if
   * the image is ALREADY in cache. Without it a cached image is `complete` on
   * mount, unblurs on the first effect, and the reveal is never seen - the
   * picture just snaps in. Default 0 keeps the original behaviour for existing
   * callers; a grid of cards sets it so the second visit animates like the
   * first.
   */
  minBlurMs?: number;
  /**
   * Easing for the blur→sharp transition. The default,
   * `cubic-bezier(0.22, 1, 0.36, 1)` (easeOutQuint), is heavily FRONT-LOADED:
   * roughly 80% of the blur is removed in the first fifth of the duration, so
   * the image reads as snapping sharp and then idling, and raising durationMs
   * mostly lengthens the part nobody can see. Pass an even curve when the
   * reveal itself is meant to be watched.
   */
  easing?: string;
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Memoized: every call site renders it repeated - marketing output grid, its
// filmstrip thumbnails, the slides deck cards, the chat reference thumbnails -
// and each unmemoized render re-runs window.matchMedia() and rebuilds the blur
// style object, once per tile, on any parent render.
export const BlurRevealImage = memo(function BlurRevealImage({
  src,
  alt = '',
  className,
  imgClassName,
  onReady,
  onError,
  onClick,
  draggable = false,
  loading,
  fetchPriority,
  blurPx = 32,
  durationMs = 600,
  minBlurMs = 0,
  easing = 'cubic-bezier(0.22, 1, 0.36, 1)',
}: BlurRevealImageProps) {
  // The src whose FULL bytes have loaded. Sharp only when it matches the current
  // src; any src change flips us back to blurred until that new src finishes.
  const [readySrc, setReadySrc] = useState<string | null>(null);
  const ready = readySrc === src;
  const imgRef = useRef<HTMLImageElement>(null);
  const onReadyRef = useRef(onReady);
  // Synced in an effect, not during render: writing a ref while rendering is a
  // side effect (react-hooks/refs). The ref is only ever READ from the load
  // handler, which runs after paint, so the effect always wins the race.
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  // When this src started loading, so a cached image can still be held blurred
  // for minBlurMs instead of snapping sharp on mount. Seeded at 0 and stamped in
  // the effect below: Date.now() in the initialiser is a render-time impure call
  // (react-hooks/purity), and the value it produced was overwritten by that same
  // effect on mount anyway.
  const startedAtRef = useRef(0);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    startedAtRef.current = Date.now();
    return () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    };
  }, [src]);

  // Mark the current src as loaded → unblur. `load` handles network images and the
  // one-time `complete` check below handles a cached image whose event already fired.
  // Do not poll: lazy off-screen images may intentionally remain incomplete for a
  // long time, and one timer per image made large List histories continuously busy.
  const markReady = () => {
    const img = imgRef.current;
    if (img && img.naturalWidth > 0) {
      // Fires as soon as the bytes are in, NOT when the blur finishes: callers
      // use this to drop their own placeholder, and the blurred image should be
      // what replaces it.
      onReadyRef.current?.({ naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight });
    }
    // `|| Date.now()` covers the ordering the 0 seed opens up: a CACHED image can
    // fire load before the mount effect has stamped the start, and an unguarded
    // 0 would read as "started at the epoch", make `remaining` hugely negative
    // and snap the image sharp instantly - precisely the flash this component
    // exists to prevent, and only on cached images, so it would look like a
    // flake rather than a bug.
    const startedAt = startedAtRef.current || Date.now();
    const remaining = minBlurMs - (Date.now() - startedAt);
    if (remaining <= 0) {
      setReadySrc(src);
      return;
    }
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => setReadySrc(src), remaining);
  };
  useEffect(() => {
    const el = imgRef.current;
    if (!el?.complete) return;
    if (el.naturalWidth > 0) markReady();
    else onError?.(); // complete but zero-size = broken image.
  }, [src]);

  const reduce = prefersReducedMotion();

  return (
    <div className={clsx('overflow-hidden', className)} onClick={onClick}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={draggable}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        onLoad={markReady}
        onError={onError}
        className={clsx('absolute inset-0 h-full w-full', imgClassName)}
        style={
          reduce
            ? undefined
            : {
                filter: ready ? 'blur(0px)' : `blur(${blurPx}px)`,
                transform: ready ? 'scale(1)' : 'scale(1.06)',
                transition: `filter ${durationMs}ms ${easing}, transform ${durationMs}ms ${easing}`,
                willChange: 'filter, transform',
              }
        }
      />
    </div>
  );
});

/**
 * useBlurRevealFilter, the same "hold a blur until the whole image is in, then snap
 * sharp" reveal as BlurRevealImage, but for an INTRINSIC-SIZE <img> (e.g. inline chat
 * images sized `max-width:100%; height:auto`) that cannot use BlurRevealImage's
 * absolute-fill wrapper. Spread the returned ref/onLoad/onError onto the <img> and
 * merge `style` into its inline style. Blur-only (no scale) so it never affects layout
 * or bleeds outside a wrapper-less image. Un-blurs once the image SETTLES (loaded or
 * errored) so a broken image can't stay blurred. Honors prefers-reduced-motion.
 */
export function useBlurRevealFilter(src: string, opts?: { blurPx?: number; durationMs?: number }) {
  const { blurPx = 24, durationMs = 600 } = opts ?? {};
  const ref = useRef<HTMLImageElement | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // For a CACHED image the load event can fire before React attaches onLoad, so read
    // `complete` on src change. `complete` (not naturalWidth) is the settle signal.
    const el = ref.current;
    setReady(Boolean(el && el.complete));
  }, [src]);
  const style: CSSProperties = prefersReducedMotion()
    ? {}
    : {
        filter: ready ? 'blur(0px)' : `blur(${blurPx}px)`,
        transition: `filter ${durationMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        willChange: 'filter',
      };
  return { ref, onLoad: () => setReady(true), onError: () => setReady(true), style } as const;
}
